import { TreeHash, Direction, DirVectors, LNode, LPoint, IGraph } from "../types";
import Graph from "../Graph";

export default class SymmetricTree {
	private _step: number;
	constructor() {
		this._step = 0;
	}

	public layout(g: IGraph, config) {
		g.root(g.toTree(g.hash[config.root]));

		const nodes = g.getNodes();

		config.levelPadding = config.levelPadding || this._getStep(nodes);
		config.itemPadding = config.itemPadding || this._getStep(nodes);
		config.dir = config.dir || Direction.Bottom;
		config.rotate = config.dir === Direction.Right || config.dir === Direction.Left;

		const vector = DirVectors[config.dir];
		this._layout(g, config);

		g.setBox();

		if (config.dir !== Direction.Bottom) {
			g.rotate(vector);
		}

		const box = g.getBox();
		g.translate({ x: box[0][0] * -1, y: box[1][0] * -1 });
		g.setBox();

		return g;
	}

	private _layout(g: IGraph, config): string {
		const root = g.getRoot();
		root.x = root.y = 0;
		if (g.getLevels().length == 1) {
			g._symmetry = true;
			return "0";
		}

		const ctrees = root.kids.map(a => new Graph(g, a, config));
		const itree: TreeHash = {};
		ctrees.forEach(c => {
			const code = this._layout(c, config);
			if (!itree[code]) {
				itree[code] = [c];
			} else {
				itree[code].push(c);
			}
		});

		const classes = Object.keys(itree).sort((a, b) => {
			const ta = itree[a][0];
			const tb = itree[b][0];

			const wa = ta._width;
			const wb = tb._width;
			if (wa > wb) return -1;
			if (wa < wb) return 1;

			const da = ta.getLevels().length;
			const db = tb.getLevels().length;
			if (da > db) return -1;
			if (da < db) return 1;

			return a < b ? 1 : -1;
		});

		// get number of non-paired sub-graphs
		let oddNumber = 0;
		let oddClass = 0;
		for (let i = 0; i < classes.length; i++) {
			if (itree[classes[i]].length % 2 === 1) {
				oddNumber++;
				oddClass = i;
			}
		}

		// detect symmetry
		let center = false;
		let symmetry = false;
		if (oddNumber == 1) {
			const oddTree = itree[classes[oddClass]][0];
			// [FIXME] probably any tree with balanced bounds need to be counted as symmetric
			if (oddTree._symmetry) {
				// center symmetric tree
				symmetry = true;
			}
			center = true;
			if (oddClass !== 0) {
				classes.unshift(classes[oddClass]);
				classes.splice(oddClass + 1, 1);
			}
		} else if (oddNumber === 0) {
			symmetry = true;
		}
		g._symmetry = symmetry;

		this._layout_place(g, itree, classes, center, config);
		return g.getIString();
	}

	private _layout_place(g: IGraph, itree: TreeHash, classes: string[], center: boolean, config) {
		const dir: LPoint = { x: 0, y: config.levelPadding };
		let next = true;
		for (let i = 0; i < classes.length; i++) {
			const trees = itree[classes[i]];
			trees.forEach(t => {
				if (center) {
					// only first tree can be placed in the center
					center = false;

					//move in position
					t.translate(dir);

					// update bounds
					let sl = 0,
						su = 0;
					const tb = t.getLevelBounds();
					for (let j = 0; j < tb.length; j++) {
						const [l, u] = tb[j];
						g._bounds[j + 1] = [l, u];
						if (l < sl) sl = l;
						if (u > su) su = u;
					}

					g._tbounds = [sl, su];
				} else {
					const xu = next ? 1 : 0;
					const xl = next ? 0 : 1;

					if (next) t.mirror();

					let treeMaxBound, pos;
					pos = treeMaxBound = 999999 * (next ? -1 : 1);

					const padding = config.itemPadding;
					for (let i = 0; i < t._bounds.length; i++) {
						const test =
							g.getBounds(i + 1, padding, config.wide)[xu] -
							t.getBounds(i, padding, config.wide)[xl];
						if ((next && test > pos) || (!next && test < pos)) {
							pos = test;
						}
					}

					// move tree to the calculated position
					t.translate({ x: pos, y: dir.y });

					// update bounds
					for (let i = 0; i < g._bounds.length; i++) {
						let maxBound;
						if (i == 0 || i > t._bounds.length) {
							// level absent in sub-tree
							maxBound = g._bounds[i][xu];
						} else {
							// level present in sub-tree
							maxBound = g._bounds[i][xu] = t._bounds[i - 1][xu];
						}

						if ((next && maxBound > treeMaxBound) || (!next && maxBound < treeMaxBound)) {
							treeMaxBound = maxBound;
						}
					}

					g._tbounds[xu] = treeMaxBound;
					next = !next;
				}
			});
		}
	}

	private _getStep(nodes: LNode[]): number {
		if (this._step === 0) {
			const count = nodes.length;

			// 2 x Average Dimension
			let sum = 0;
			nodes.forEach(n => (sum += n.w + n.h));
			this._step = sum / count;
		}

		return this._step;
	}
}
